import { useEffect, useMemo, useRef, useState } from "react";
import { InviteQueue } from "@/components/followup/InviteQueue";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { groupByRoom, suggestedFollowUps } from "@omina/core";
import type { Contact, OutcomeResult } from "@omina/core";
import { api, apiError } from "../lib/api";
import { readSseText } from "../lib/stream";
import {
  eventsQuery,
  useAddContact,
  useContacts,
  useFeeds,
  useLogOutcome,
  useMoves,
  useOutcomes,
  useProfile,
  useRecordMove,
  useSaveFeed,
} from "../lib/queries";

/**
 * Follow up — beat 3, and the thinnest complete loop in the product.
 *
 * Add who you met → research and draft in her voice → she sends it herself →
 * one tap for what came back. The outcome is the whole point: a version that
 * drafts and never learns is a text generator.
 *
 * On sending: Omina does not. `/api/gmail/*` answers 501 by design until the
 * integrations phase, so the honest move is copy-to-clipboard or a `mailto:`
 * into her own client, and she says when it went. LinkedIn is manual forever
 * — rule #5, it is a ToS violation and a ban risk, not a missing feature.
 */
export const Route = createFileRoute("/followup")({
  validateSearch: z.object({
    // Handoff from /circle: "draft the follow-up for this person".
    contact: z.string().optional().catch(undefined),
  }),
  component: Followup,
});

const OUTCOMES: Array<{ result: OutcomeResult; label: string }> = [
  { result: "replied", label: "Replied" },
  { result: "meeting", label: "Meeting" },
  { result: "converted", label: "Converted" },
  { result: "no_reply", label: "No reply" },
];

/** linkedin.com/in/mike-ramos-1a2b3 → "Mike Ramos". A guess she can edit —
 *  the slug is the only thing a LinkedIn URL will ever tell us, because the
 *  page itself is never fetched. */
function nameFromLinkedin(url: string): string | null {
  const match = url.match(/linkedin\.com\/in\/([a-zA-Z0-9%-]+)/);
  if (!match?.[1]) return null;
  const parts = decodeURIComponent(match[1])
    .split("-")
    .filter((p) => p && !/^\d+$/.test(p) && !/^[0-9a-f]{5,}$/i.test(p));
  if (!parts.length) return null;
  return parts.map((p) => p[0]!.toUpperCase() + p.slice(1)).join(" ");
}

/** Drafts come back as "Subject: …\n\nbody". Split so the subject can go in
 *  the mail client's subject line instead of its first paragraph. */
function splitDraft(text: string): { subject: string; body: string } {
  const match = text.match(/^Subject:\s*(.+)\n+([\s\S]*)$/i);
  return match
    ? { subject: match[1]!.trim(), body: match[2]!.trim() }
    : { subject: "Following up", body: text.trim() };
}

function Followup() {
  const { contact: handoffId } = Route.useSearch();

  const profile = useProfile();
  const contacts = useContacts();
  const moves = useMoves();
  const outcomes = useOutcomes();

  const addContact = useAddContact();
  const recordMove = useRecordMove();
  const logOutcome = useLogOutcome();

  // ── the form ────────────────────────────────────────────────
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [context, setContext] = useState("");

  // ── the draft ───────────────────────────────────────────────
  const [draftFor, setDraftFor] = useState<Contact | null>(null);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [draftMode, setDraftMode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Ask for a draft and render it as it arrives.
   *
   * The tokens are appended to state one chunk at a time rather than awaited
   * and set once: the request takes ~20s at Sonnet, and watching the message
   * appear is the difference between "it is working" and a dead spinner.
   */
  /**
   * `research` is passed by the caller, never assumed.
   *
   * The API defaults it to false. That default is the fix for a real bug: this
   * route used to search the public web for whoever it was drafting to
   * whenever nothing was cached — spending money and writing notes about a
   * person the founder never asked us to look up. Research is a decision she
   * makes about a person; drafting is not consent to it.
   */
  async function generateDraft(contact: Contact, research = false) {
    setDraftFor(contact);
    setDraft("");
    setError(null);
    setCopied(false);
    setStreaming(true);
    try {
      const res = await api.draft.$post({
        json: { contactId: contact.id, ...(research ? { research: true } : {}) },
      });
      if (!res.ok) throw await apiError(res);
      setDraftMode(res.headers.get("x-omina-mode"));
      await readSseText(res.body, (delta) => setDraft((current) => current + delta));
    } catch (e) {
      setError(e instanceof Error ? e.message : "the draft failed");
    } finally {
      setStreaming(false);
    }
  }

  /** Set when a draft is waiting on her answer to "research first?". */
  const [askResearch, setAskResearch] = useState<Contact | null>(null);

  /** The two answers, both of which draft — they differ only in whether the
   *  web is searched first. Neither leaves her without a draft. */
  function answerResearch(research: boolean) {
    const contact = askResearch;
    setAskResearch(null);
    if (contact) void generateDraft(contact, research);
  }

  /** Straight to a draft when research already exists (she said yes once, and
   *  re-reading it is free); otherwise ask, because the alternative is
   *  spending her money on a decision she did not make. */
  function startDraft(contact: Contact) {
    if (contact.researchedAt || contact.enrichment) {
      void generateDraft(contact);
      return;
    }
    setAskResearch(contact);
  }

  // The handoff from /circle. `once` guards against re-firing when the
  // contacts query refetches — a second draft would replace one she is
  // already editing.
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!handoffId || handled.current === handoffId) return;
    const contact = contacts.data?.find((c) => c.id === handoffId);
    if (!contact) return;
    handled.current = handoffId;
    startDraft(contact);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffId, contacts.data]);

  async function addAndDraft() {
    if (!name.trim() || !context.trim()) return;
    setError(null);
    try {
      const contact = await addContact.mutateAsync({
        name: name.trim(),
        context: context.trim(),
        company: company.trim() || null,
        role: role.trim() || null,
        email: email.trim() || null,
        linkedinUrl: linkedinUrl.trim() || null,
      });
      setLinkedinUrl("");
      setName("");
      setCompany("");
      setRole("");
      setEmail("");
      setContext("");
      startDraft(contact);
    } catch (e) {
      setError(e instanceof Error ? e.message : "couldn't add them");
    }
  }

  /** She is taking the draft out to send it. Record the move, which also
   *  moves the contact out of "new" — same rule as the server's.
   *
   *  Awaited, not fired and forgotten. The outcome buttons appear as soon as a
   *  move exists in the cache, and the optimistic one carries a client-made
   *  id: tapping "Replied" fast enough sent the server an id it had never
   *  seen, which it rightly refused with a 409 — while the optimistic update
   *  showed the outcome as logged. Losing a one-tap outcome silently is the
   *  worst failure this screen has, because that tap is the moat. */
  async function markSent(channel: "email" | "linkedin" | "other") {
    if (!draftFor || !draft.trim()) return;
    const { subject } = splitDraft(draft);
    try {
      await recordMove.mutateAsync({ contactId: draftFor.id, draft, subject, channel });
    } catch {
      // The mutation surfaces its own error under the draft; swallowing here
      // just keeps an unhandled rejection out of the console.
    }
  }

  async function copyAndMarkSent() {
    if (!draftFor || !draft.trim()) return;
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
    } catch {
      // Clipboard permission denied, or an insecure origin. The draft is
      // still on screen and selectable, and the move still counts.
    }
    markSent(draftFor.email ? "email" : draftFor.linkedinUrl ? "linkedin" : "other");
  }

  function mailtoHref(contact: Contact): string {
    const { subject, body } = splitDraft(draft);
    // Not URLSearchParams: it encodes a space as "+", which mail clients paste
    // into the body literally. mailto wants percent-encoding throughout.
    const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    return `mailto:${encodeURIComponent(contact.email ?? "")}?${query}`;
  }

  // The move for the person on screen, if she has already sent one. Moves come
  // back newest first, so the first match is the one an outcome hangs off.
  const currentMove = draftFor
    ? moves.data?.find((m) => m.contactId === draftFor.id)
    : undefined;
  const currentOutcome = currentMove
    ? outcomes.data?.find((o) => o.moveId === currentMove.id)
    : undefined;

  const sentCount = moves.data?.length ?? 0;
  const landed = outcomes.data?.filter((o) => o.result !== "no_reply").length ?? 0;

  return (
    <>
      <div className="eyebrow">Follow up</div>
      <h1 className="tk">You met them. Now make it land.</h1>
      <p className="lede">
        Add who you met. Omina researches them and drafts the follow-up in your
        voice, aimed at your goal — you send it, then tap what came back. Every
        outcome makes the next draft sharper.
      </p>

      {profile.isSuccess && !profile.data && (
        <div className="samplebar">
          The draft is built from your profile — the brain.{" "}
          <Link to="/intake">Do the intake first →</Link>
        </div>
      )}

      {/* Above "waiting on you" on purpose: these are people she has not even
          admitted yet, so they precede the ones she has met and owes a note. */}
      <div className="mt-5.5 mb-5.5">
        <InviteQueue />
      </div>

      <Waiting />

      <div className="card">
        <div className="field">
          <label className="lbl" htmlFor="linkedin">
            Paste their LinkedIn
            <span className="muted"> — the name fills itself; everything else is optional</span>
          </label>
          <input
            id="linkedin"
            className="tk"
            value={linkedinUrl}
            onChange={(e) => {
              setLinkedinUrl(e.target.value);
              if (!name.trim()) {
                const guess = nameFromLinkedin(e.target.value);
                if (guess) setName(guess);
              }
            }}
            placeholder="linkedin.com/in/…"
          />
        </div>

        <div
          className="field"
          style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 10 }}
        >
          <div>
            <label className="lbl" htmlFor="who">
              Who you met
            </label>
            <input
              id="who"
              className="tk"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sarah Chen"
            />
          </div>
          <div>
            <label className="lbl" htmlFor="company">
              Company <span className="muted">(opt)</span>
            </label>
            <input
              id="company"
              className="tk"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Restive"
            />
          </div>
          <div>
            <label className="lbl" htmlFor="role">
              Role <span className="muted">(opt)</span>
            </label>
            <input
              id="role"
              className="tk"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Partner"
            />
          </div>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="email">
            Email <span className="muted">(opt — lets you open the draft in your mail app)</span>
          </label>
          <input
            id="email"
            className="tk"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="sarah@restive.com"
          />
        </div>

        <RoomsRail onPick={(stub) => setContext((prev) => (prev.trim() ? `${stub}${prev}` : stub))} />

        <div className="field">
          <label className="lbl" htmlFor="context">
            Where you met &amp; what you talked about
          </label>
          <textarea
            id="context"
            className="tk"
            rows={2}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="NerdCon happy hour — she asked how we handle reconciliation edge cases; mentioned their portfolio co struggles with it."
          />
        </div>

        <button
          type="button"
          className="btn"
          disabled={!name.trim() || !context.trim() || addContact.isPending || streaming}
          onClick={() => void addAndDraft()}
        >
          {addContact.isPending || streaming ? "Drafting…" : "Add & draft the follow-up"}
        </button>
      </div>

      {/*
        Asked, not assumed.
        
        Drafting used to search the public web for this person whenever nothing
        was cached — a paid call and a stored note, from an action that only
        said "write me a message". Both answers below produce a draft; they
        differ in whether anyone is looked up first.
      */}
      {askResearch && (
        <div className="card mt-5.5">
          <h3>Research {askResearch.name} first?</h3>
          <p className="why">
            Searches the public web for them and costs one lookup. Without it the draft
            uses what you wrote and the room you met in.
          </p>
          <div className="chips mt-1.5">
            <button type="button" className="chip" onClick={() => answerResearch(true)}>
              Research, then draft
            </button>
            <button type="button" className="chip" onClick={() => answerResearch(false)}>
              Just draft
            </button>
            <button type="button" className="linkbtn" onClick={() => setAskResearch(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {draftFor && (
        <div className="mt-5.5">
          <div className="eyebrow mb-2.5">
            Draft for {draftFor.name} — in your voice, edit anything
            {draftMode === "draft" ? " · template mode (no model key)" : ""}
          </div>

          {streaming ? (
            // Tokens as they land. A textarea would fight the cursor while
            // the text is still growing, so it only appears once it stops.
            <div className="card">
              <div className={`msg caret${draft ? "" : " muted"}`} style={{ maxWidth: "100%" }}>
                {draft || "Researching them and drafting…"}
              </div>
            </div>
          ) : (
            <>
              <textarea
                className="tk"
                rows={9}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label={`Follow-up draft for ${draftFor.name}`}
              />

              {currentMove ? (
                <div className="card mt-3">
                  <div className="eyebrow">Sent. What came back?</div>
                  <p className="why mt-1.5">
                    One tap. This is the part that makes the next draft sharper.
                  </p>
                  <div className="actions">
                    {OUTCOMES.map((outcome) => (
                      <button
                        key={outcome.result}
                        type="button"
                        className="chip"
                        aria-pressed={currentOutcome?.result === outcome.result}
                        // No moveId. The server attaches the outcome to this
                        // contact's newest move, which is exactly what "sent,
                        // what came back?" means — and it cannot be handed an
                        // id the client invented for a move still in flight.
                        onClick={() =>
                          logOutcome.mutate({
                            contactId: draftFor.id,
                            result: outcome.result,
                          })
                        }
                      >
                        {outcome.label}
                      </button>
                    ))}
                  </div>
                  {logOutcome.isError && <div className="danger">{logOutcome.error.message}</div>}
                  <p className="hint">
                    <Link to="/circle">See everyone in your circle →</Link>
                  </p>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn"
                    disabled={!draft.trim() || recordMove.isPending}
                    onClick={() => void copyAndMarkSent()}
                  >
                    {copied ? "Copied ✓ — marked as sent" : "Copy & mark sent"}
                  </button>

                  {draftFor.email && (
                    <div className="actions" style={{ justifyContent: "center" }}>
                      <a
                        className="chip"
                        href={mailtoHref(draftFor)}
                        onClick={() => markSent("email")}
                      >
                        Open in your email app &amp; mark sent ↗
                      </a>
                    </div>
                  )}

                  {/* Not an apology for a missing button: it is what Omina
                      does and does not do, said before she looks for a
                      Send that is not there. */}
                  <p className="hint">
                    Omina never sends for you. You paste it and send it
                    yourself — and on LinkedIn that is permanent: automating it
                    is a ToS violation and a ban risk, so it stays
                    draft-and-copy.
                  </p>
                </>
              )}
            </>
          )}

          {(error ?? recordMove.error) && (
            <div className="danger">{error ?? recordMove.error?.message}</div>
          )}
        </div>
      )}

      {(contacts.data?.length ?? 0) > 0 && (
        <div className="samplebar mt-6.5">
          <b style={{ color: "var(--foreground)" }}>Your circle</b> ·{" "}
          {contacts.data?.length} people · {sentCount} sent · {landed} landed{" "}
          <Link to="/circle">open →</Link>
        </div>
      )}
    </>
  );
}

/**
 * The queue this screen used to make her assemble in her head.
 *
 * It opened on an empty form: she had to remember who she met, find them, and
 * ask for a draft. Every piece of that was already on record — the connection
 * date from LinkedIn, the room from her own calendar, whether she has written
 * from `moves`. Crossing the three is the difference between a tool she
 * drives and one that hands her the day's work.
 *
 * Tapping a name fills the form below rather than sending anything. Rule #5
 * has not moved: a follow-up is drafted for her, never sent for her.
 */
function Waiting() {
  const contacts = useContacts();
  const moves = useMoves();
  const navigate = useNavigate({ from: "/followup" });

  const groups = useMemo(() => {
    if (!contacts.data || !moves.data) return [];
    return groupByRoom(suggestedFollowUps(contacts.data, moves.data, todayKey()));
  }, [contacts.data, moves.data]);

  if (!groups.length) return null;
  const total = groups.reduce((n, g) => n + g.people.length, 0);

  return (
    <div className="card">
      <div className="eyebrow">Waiting on you</div>
      <p className="hint mt-0.5">
        {total} {total === 1 ? "person" : "people"} you met and have not written to. Tap a
        name to load them below.
      </p>

      {groups.map((group) => (
        <div key={`${group.eventTitle}|${group.metOn}`} className="mt-2.5">
          <div className="hint mb-1">
            {group.eventTitle ?? "Connected"} ·{" "}
            {group.daysAgo === 0
              ? "today"
              : group.daysAgo === 1
                ? "yesterday"
                : `${group.daysAgo} days ago`}
          </div>
          <div className="chips">
            {group.people.map((person) => (
              <button
                key={person.id}
                type="button"
                className="chip"
                // The same handoff the circle uses — the form below reads
                // `?contact=` and fills itself from the row.
                onClick={() => void navigate({ search: { contact: person.id } })}
              >
                {person.name}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Local calendar day, matching what the API and `metAt` store. */
function todayKey(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * "Where you met", from her own calendars.
 *
 * The feed URL is a bearer credential — anyone holding a Google secret iCal
 * address can read that calendar — so it is stored encrypted and never read
 * back out. That is why this can offer to replace a feed but never to show
 * one, and why the prototype's localStorage copy of it is gone.
 */
function RoomsRail({ onPick }: { onPick: (stub: string) => void }) {
  const feeds = useFeeds();
  const saveFeed = useSaveFeed();

  // No feed, no events — and asking anyway would spend a request per page view
  // on an answer that is empty by construction for everyone who has not
  // connected a calendar.
  const events = useQuery({
    ...eventsQuery(30, 7),
    enabled: (feeds.data?.length ?? 0) > 0,
  });

  const [open, setOpen] = useState(false);
  const [gcal, setGcal] = useState("");
  const [luma, setLuma] = useState("");

  const connected = feeds.data ?? [];
  const upcoming = events.data?.events ?? [];

  return (
    <>
      {upcoming.length > 0 && (
        <div className="field">
          <span className="lbl">
            Where you met <span className="muted">— from your calendars, tap one</span>
          </span>
          <div className="chips">
            {upcoming.slice(0, 8).map((event) => {
              const day = new Date(event.start).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              });
              return (
                <button
                  key={`${event.title}-${event.start}`}
                  type="button"
                  className="chip"
                  onClick={() =>
                    onPick(
                      `Met at ${event.title}${event.location ? ` · ${event.location}` : ""} (${day}). `,
                    )
                  }
                >
                  <span className="dot" aria-hidden />
                  {event.title.slice(0, 34)}
                  <span className="muted">{day}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="field">
        <button type="button" className="linkbtn" onClick={() => setOpen(!open)}>
          {open
            ? "Hide calendar feeds ▴"
            : connected.length
              ? `Calendar feeds · ${connected.map((f) => f.kind).join(", ")} ▾`
              : "Connect your rooms — Google Calendar + Luma ▾"}
        </button>

        {open && (
          <div className="mt-3">
            <div className="field">
              <label className="lbl" htmlFor="gcal">
                Google Calendar — secret iCal address
              </label>
              <input
                id="gcal"
                className="tk"
                value={gcal}
                onChange={(e) => setGcal(e.target.value)}
                placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
              />
              <p className="why">
                Google Calendar → Settings → your calendar → &ldquo;Secret address in
                iCal format&rdquo;.
              </p>
            </div>

            <div className="field">
              <label className="lbl" htmlFor="luma">
                Luma — calendar feed URL
              </label>
              <input
                id="luma"
                className="tk"
                value={luma}
                onChange={(e) => setLuma(e.target.value)}
                placeholder="https://api.lu.ma/ics/get?entity=calendar&…"
              />
            </div>

            <button
              type="button"
              className="btn"
              disabled={saveFeed.isPending || (!gcal.trim() && !luma.trim())}
              onClick={async () => {
                if (gcal.trim()) await saveFeed.mutateAsync({ kind: "gcal", url: gcal.trim() });
                if (luma.trim()) await saveFeed.mutateAsync({ kind: "luma", url: luma.trim() });
                setGcal("");
                setLuma("");
                setOpen(false);
              }}
            >
              {saveFeed.isPending ? "Saving…" : "Save & load my rooms"}
            </button>

            {saveFeed.isError && <div className="danger">{saveFeed.error.message}</div>}

            <p className="hint">
              These addresses are credentials, so they are encrypted and never
              shown again — you can replace one, not read it back.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
